import { GoogleDeviceStatus } from '@cdw-ae/common-data';
import { getDateOffsetInDays } from '@cdw-ae/date-time-service';
import { NavigationConfig, UrlStateItem } from '@cdw-ae/url-params';
import { ChartType } from '../../../../components/charts/models/chart.model';
import { CardType } from '../../../../layouts/internal/components/dashboard/dashboard.model';
import { REPORT_TYPES } from '../../../../layouts/internal/components/reports/reports.model';

export const DashboardNavigationConfig: NavigationConfig<UrlStateItem>[] = [
  {
    identifier: CardType.DEVICE_LAST_ACTIVE_CARD,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'devices',
        filters: [
          {
            field: 'status',
            operator: 'equals',
            value: GoogleDeviceStatus.INACTIVE,
          },
        ],
      },
    ],
  },
  {
    identifier: ChartType.DEVICE_INACTIVITY,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: '> 7 days ago',
        filters: [
          {
            field: 'lastSync',
            operator: 'between',
            value: getDateOffsetInDays(-14),
            value2: getDateOffsetInDays(-8),
          },
        ],
      },
      {
        clickedValue: '> 14 days ago',
        filters: [
          {
            field: 'lastSync',
            operator: 'between',
            value: getDateOffsetInDays(-30),
            value2: getDateOffsetInDays(-15),
          },
        ],
      },
      {
        clickedValue: '> 30 days ago',
        filters: [
          {
            field: 'lastSync',
            operator: 'lessThan',
            value: getDateOffsetInDays(-31),
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.AUTO_UPDATE_EXPIRY_CARD,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'devices',
        filters: [],
      },
    ],
  },
  {
    identifier: ChartType.AUTO_UPDATE_EXPIRY,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'Past AUE',
        filters: [
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'lessThan',
            value: new Date().toISOString(),
          },
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'is',
            value: 'missing',
          }
        ],
      },
      {
        clickedValue: '< 3 months',
        filters: [
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'between',
            value: getDateOffsetInDays(1),
            value2: getDateOffsetInDays(90),
          },
        ],
      },
      {
        clickedValue: '< 6 months',
        filters: [
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'between',
            value: getDateOffsetInDays(91),
            value2: getDateOffsetInDays(180),
          },
        ],
      },
      {
        clickedValue: '< 12 months',
        filters: [
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'between',
            value: getDateOffsetInDays(181),
            value2: getDateOffsetInDays(365),
          },
        ],
      },
      {
        clickedValue: '> 12 months',
        filters: [
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'greaterThan',
            value: getDateOffsetInDays(365),
          },
        ],
      },
    ],
  },
  {
    identifier: ChartType.BATTERY_HEALTH,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'Poor Battery Health',
        filters: [
          {
            field: 'status',
            operator: 'equals',
            value: GoogleDeviceStatus.ACTIVE,
          },
          {
            field: 'batteryHealth',
            operator: 'oneOf',
            value: JSON.stringify(['SOON', 'NOW']),
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.POOR_BATTERY_HEALTH_CARD,
    targetRoute: '/report-viewer/battery-health',
    targetFilterBarId: 'reportFilters',
    filterMappings: [
      {
        clickedValue: 'default',
        filters: [],
      },
    ],
  },
  {
    identifier: ChartType.STORAGE_USAGE,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'Storage > 80% Full',
        filters: [
          {
            field: 'status',
            operator: 'equals',
            value: GoogleDeviceStatus.ACTIVE,
          },
          {
            field: 'vendorProperties.google.diskUsedCategory',
            operator: 'oneOf',
            value: JSON.stringify(['80', '90']),
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.STORAGE_USAGE_OVER_80_CARD,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    workflowType: 'profile-wipe',
    filterMappings: [
      {
        clickedValue: 'devices',
        filters: [
          {
            field: 'vendorProperties.google.diskUsedCategory',
            operator: 'oneOf',
            value: JSON.stringify(['80', '90']),
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.OS_VERSION_OVER_3_BEHIND_CARD,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'os-version-over-3-behind',
        filters: [
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: '3+ OS Versions Behind',
          },
        ],
      },
      {
        clickedValue: '3+ OS Versions Behind',
        filters: [
          {
            field: 'status',
            operator: 'equals',
            value: GoogleDeviceStatus.ACTIVE,
          },
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: '3+ OS Versions Behind',
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.AUE_IN_90_DAYS_CARD,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'devices',
        filters: [
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'between',
            value: new Date().toISOString(),
            value2: getDateOffsetInDays(90),
          },
        ],
      },
      {
        clickedValue: 'AUE in Next 90 Days',
        filters: [
          {
            field: 'status',
            operator: 'equals',
            value: GoogleDeviceStatus.ACTIVE,
          },
          {
            field: 'vendorProperties.google.autoUpdateThrough',
            operator: 'between',
            value: new Date().toISOString(),
            value2: getDateOffsetInDays(90),
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.DEVICE_OS_VERSION_STATUS_CARD,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'os-version-over-3-behind',
        filters: [
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: '3+ OS Versions Behind',
          },
        ],
      },
    ],
  },
  {
    identifier: ChartType.OS_VERSION_STATE,
    targetRoute: '/deviceSearch',
    targetFilterBarId: 'deviceSearch',
    filterMappings: [
      {
        clickedValue: 'Latest OS',
        filters: [
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: 'Latest OS',
          },
        ],
      },
      {
        clickedValue: '1 OS Version Behind',
        filters: [
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: '1 OS Version Behind',
          },
        ],
      },
      {
        clickedValue: '2 OS Versions Behind',
        filters: [
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: '2 OS Versions Behind',
          },
        ],
      },
      {
        clickedValue: '3+ OS Versions Behind',
        filters: [
          {
            field: 'osVersionStatus',
            operator: 'equals',
            value: '3+ OS Versions Behind',
          },
        ],
      },
    ],
  },
  {
    identifier: CardType.SINGLE_USER_REPORT_CARD,
    targetRoute: '/reports',
    targetFilterBarId: 'reports',
    workflowType: 'singleUser',
    filterMappings: [
      {
        clickedValue: 'reports',
        filters: [],
      },
    ],
  },
  {
    identifier: CardType.SINGLE_DEVICE_REPORT_CARD,
    targetRoute: '/reports',
    targetFilterBarId: 'reports',
    workflowType: 'singleDevice',
    filterMappings: [
      {
        clickedValue: 'reports',
        filters: [],
      },
    ],
  },
  {
    identifier: CardType.AGGREGATE_REPORTS_CARD,
    targetRoute: '/reports',
    filterMappings: [
      {
        clickedValue: 'reports',
        filters: [],
      },
    ],
  },
  // Aggregate report navigation configs (for dashboard dropdown)
  {
    identifier: REPORT_TYPES.MODEL_AUE,
    targetRoute: '/report-viewer/model-aue',
    targetFilterBarId: 'reportFilters',
    filterMappings: [
      {
        clickedValue: 'default',
        filters: [],
      },
    ],
  },
  {
    identifier: REPORT_TYPES.CHROME_VERSION_AGE,
    targetRoute: '/report-viewer/chrome-version-age',
    targetFilterBarId: 'reportFilters',
    filterMappings: [
      {
        clickedValue: 'default',
        filters: [],
      },
    ],
  },
  {
    identifier: REPORT_TYPES.BATTERY_HEALTH,
    targetRoute: '/report-viewer/battery-health',
    targetFilterBarId: 'reportFilters',
    filterMappings: [
      {
        clickedValue: 'default',
        filters: [],
      },
    ],
  },
  {
    identifier: REPORT_TYPES.STORAGE_BY_OU,
    targetRoute: '/report-viewer/storage-by-ou',
    targetFilterBarId: 'reportFilters',
    filterMappings: [
      {
        clickedValue: 'default',
        filters: [],
      },
    ],
  },
  {
    identifier: REPORT_TYPES.FIFTEEN_DAY_ACTIVITY,
    targetRoute: '/report-viewer/fifteen-day-activity',
    targetFilterBarId: 'reportFilters',
    filterMappings: [
      {
        clickedValue: 'default',
        filters: [],
      },
    ],
  },
];
